import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_AUDIO_LANGUAGE,
  type AudioLanguage,
} from "../actions/audio-transcription/createAudioTranscription";
import { getUserPreferences, updateUserPreferences } from "../actions/users/preferences";

/**
 * Audio/record language selection backed by the user's saved server-side
 * preference (Trello #401). On mount it adopts the saved default (falling back
 * to DEFAULT_AUDIO_LANGUAGE when unset or unreachable); every change is
 * persisted best-effort so the choice survives logout/login.
 *
 * Shared by RecordTab + AudioTab so the load-and-persist logic lives in one
 * place rather than being forked into each tab.
 *
 * `locked` freezes the displayed language: while true, a saved preference that
 * arrives (initial load or reload) is held back instead of applied, and is
 * re-read as soon as `locked` turns false. RecordTab locks it while a recording
 * is live or a recorded clip waits to be submitted, so a language changed on
 * another tab cannot rewrite the language of that recording. An explicit
 * setLanguage still applies, and it also supersedes any load already in flight and any
 * held-back refresh.
 */
export function useAudioLanguagePreference(options: { locked?: boolean } = {}): {
  language: AudioLanguage;
  setLanguage: (next: AudioLanguage) => void;
  /** Re-reads the saved preference, e.g. after another tab changed it while this one was hidden. */
  reload: () => void;
} {
  const [language, setLanguageState] = useState<AudioLanguage>(DEFAULT_AUDIO_LANGUAGE);
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((c) => c + 1), []);
  const locked = options.locked ?? false;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  // Bumped by setLanguage so a load that started earlier cannot overwrite the explicit pick.
  const explicitChoiceRef = useRef(0);
  const heldBackRef = useRef(false);

  // A saved value that arrived while locked was dropped; fetch it again on unlock.
  useEffect(() => {
    if (!locked && heldBackRef.current) {
      heldBackRef.current = false;
      reload();
    }
  }, [locked, reload]);

  // Adopt the saved preference once it loads. A missing record / unreachable
  // backend leaves the default in place (graceful degradation).
  useEffect(() => {
    let cancelled = false;
    const choiceAtStart = explicitChoiceRef.current;
    getUserPreferences()
      .then((res) => {
        if (cancelled || !res.data.audioLanguage) return;
        if (explicitChoiceRef.current !== choiceAtStart) return;
        if (lockedRef.current) {
          heldBackRef.current = true;
          return;
        }
        setLanguageState(res.data.audioLanguage);
      })
      .catch(() => {
        /* no saved preference / backend unavailable — keep the default */
      });
    return () => {
      cancelled = true;
    };
  }, [reloadCount]);

  const setLanguage = (next: AudioLanguage): void => {
    explicitChoiceRef.current += 1;
    // A saved value held back while locked is older than this choice: drop the deferred
    // re-read so unlocking cannot replace the explicit pick with it.
    heldBackRef.current = false;
    setLanguageState(next);
    void updateUserPreferences({ audioLanguage: next }).catch(() => {
      /* best-effort persist; the selection still applies to this session */
    });
  };

  return { language, setLanguage, reload };
}
